// Iter 81.1 / Phase E0.1 — Public tokenised document acceptance page.
// Anonymous (no auth). Reached from the link generated by HR via
// POST /api/hr/document/{id}/share/token. The page:
//   1) Loads the document via GET /api/hr/document/accept/{token}
//   2) Records a view via POST /accept/{token}/view
//   3) Lets the candidate Accept or Decline
//   4) Shows a confirmation card with the embedded audit stamp
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, ShieldCheck, Warning, FileText } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lightweight axios instance for anonymous endpoints — does NOT inject
// any auth header so the token-acceptance flow works without a login.
const anonApi = axios.create({ baseURL: API_BASE, timeout: 30000 });

export default function PublicAcceptDocument() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [doc, setDoc] = useState(null);
    const [emp, setEmp] = useState(null);
    const [mode, setMode] = useState("review"); // review | confirm-accept | confirm-decline | accepted | declined
    const [remarks, setRemarks] = useState("");
    const [ack, setAck] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await anonApi.get(`/hr/document/accept/${token}`);
            setDoc(data.document);
            setEmp(data.employee);
            // Best-effort view recording — non-blocking.
            anonApi.post(`/hr/document/accept/${token}/view`).catch(() => {});
            // If already accepted/declined, jump to the result state.
            if (data.document.accepted_at) setMode("accepted");
            else if (data.document.declined_at) setMode("declined");
        } catch (e) {
            // If the caller has JUST accepted/declined in this same
            // session, do NOT replace the success card with the error
            // state — the token has flipped to "used" server-side and
            // the second GET will return 410. Keep the success render.
            if (mode === "accepted" || mode === "declined") {
                return;
            }
            const detail = e?.response?.data?.detail || "Unable to load this link.";
            setError(detail);
        } finally {
            setLoading(false);
        }
    }, [token, mode]);

    useEffect(() => { Promise.resolve().then(() => { load(); }); }, [load]);

    const doAccept = async () => {
        if (!ack) return toast.error("Please acknowledge before accepting.");
        setBusy(true);
        try {
            await anonApi.post(`/hr/document/accept/${token}/accept`, { remarks });
            toast.success("Document accepted. Thank you.");
            // Pivot UI BEFORE attempting any refresh — the now-used
            // token will start returning 410 on subsequent GETs and we
            // must keep the success card visible (operator-required UX).
            setMode("accepted");
        } catch (e) {
            const detail = e?.response?.data?.detail || "Acceptance failed.";
            toast.error(detail);
        } finally {
            setBusy(false);
        }
    };

    const doDecline = async () => {
        if (remarks.trim().length < 3) return toast.error("Please provide a reason for declining.");
        setBusy(true);
        try {
            await anonApi.post(`/hr/document/accept/${token}/decline`, { remarks });
            toast.success("Document declined.");
            setMode("declined");
        } catch (e) {
            const detail = e?.response?.data?.detail || "Decline failed.";
            toast.error(detail);
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-sm text-slate-500" data-testid="public-accept-loading">Loading document…</div>;
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
                <div className="max-w-md w-full bg-white rounded-xl border border-rose-200 p-6 text-center" data-testid="public-accept-error">
                    <Warning size={36} className="mx-auto text-rose-500 mb-2" />
                    <div className="font-semibold text-slate-900 mb-1">Unable to load this link</div>
                    <div className="text-sm text-slate-600">{error}</div>
                    <div className="text-xs text-slate-400 mt-3">If you believe this is an error, please contact HR for a fresh link.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-accept-page">
            <div className="max-w-3xl mx-auto">
                <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck size={20} className="text-indigo-600" />
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">REEDOS · Secure Document Review</div>
                    </div>
                    <h1 className="text-xl font-bold text-slate-900">{doc.rendered_title}</h1>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{doc.category}</Badge>
                        <span>v{doc.template_version}</span>
                        <span>·</span>
                        <span>For: {emp.full_name} ({emp.employee_code})</span>
                        {doc.issue_date && <><span>·</span><span>Issued {doc.issue_date}</span></>}
                        {doc.effective_date && <><span>·</span><span>Effective {doc.effective_date}</span></>}
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4" data-testid="public-accept-doc">
                    <div dangerouslySetInnerHTML={{ __html: doc.rendered_html }} />
                </div>

                {mode === "review" && (
                    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="public-accept-actions">
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                            <FileText size={16} /> Action
                        </div>
                        <p className="text-xs text-slate-500">By accepting, you confirm that you have read and understood the contents of this document. Acceptance is recorded with a timestamp and your IP address for audit purposes (IP is stored privately and is never displayed on the document itself).</p>
                        <label className="flex items-start gap-2 text-sm text-slate-700">
                            <input data-testid="public-accept-ack" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" />
                            <span>I have read and accept this document.</span>
                        </label>
                        <Textarea
                            data-testid="public-accept-remarks"
                            placeholder="Optional remarks (visible to HR)…"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            rows={3}
                        />
                        <div className="flex flex-wrap gap-2">
                            <Button
                                data-testid="public-accept-btn"
                                onClick={doAccept}
                                disabled={!ack || busy}
                                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                            >
                                <CheckCircle size={16} /> Accept
                            </Button>
                            <Button
                                data-testid="public-decline-btn"
                                onClick={() => setMode("confirm-decline")}
                                variant="outline"
                                className="gap-2 text-rose-700 border-rose-200 hover:bg-rose-50"
                            >
                                <XCircle size={16} /> Decline
                            </Button>
                        </div>
                    </div>
                )}

                {mode === "confirm-decline" && (
                    <div className="bg-white rounded-xl border border-rose-200 p-5 space-y-3" data-testid="public-decline-confirm">
                        <div className="text-sm font-semibold text-slate-800">Reason for declining</div>
                        <p className="text-xs text-slate-500">Please share why you are declining this document. HR will review your response.</p>
                        <Textarea
                            data-testid="public-decline-remarks"
                            placeholder="Required — minimum 3 characters"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            rows={4}
                        />
                        <div className="flex gap-2">
                            <Button data-testid="public-decline-confirm-btn" onClick={doDecline} disabled={busy || remarks.trim().length < 3} className="bg-rose-600 hover:bg-rose-700">Confirm Decline</Button>
                            <Button onClick={() => setMode("review")} variant="outline">Back</Button>
                        </div>
                    </div>
                )}

                {mode === "accepted" && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center" data-testid="public-accept-success">
                        <CheckCircle size={32} className="mx-auto text-emerald-600 mb-2" />
                        <div className="font-semibold text-slate-900">Document accepted</div>
                        <div className="text-xs text-slate-600 mt-1">A copy of this document, including the audit stamp, has been recorded. HR will follow up with next steps.</div>
                    </div>
                )}

                {mode === "declined" && (
                    <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-center" data-testid="public-decline-success">
                        <XCircle size={32} className="mx-auto text-rose-600 mb-2" />
                        <div className="font-semibold text-slate-900">Document declined</div>
                        <div className="text-xs text-slate-600 mt-1">Your response has been recorded. HR will reach out to you shortly.</div>
                    </div>
                )}
            </div>
        </div>
    );
}
